import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Helps re-apply Google Translate to dynamically loaded content (e.g. after scroll/fetch).
 * Call retranslateForNewContent() after new data is rendered so the new DOM gets translated.
 */
@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  /** Emitted after retranslate was triggered so app can e.g. hide desktop iframes again. */
  readonly afterRetranslate$ = new Subject<void>();

  /**
   * Returns the current Google Translate target language code from the googtrans cookie, or null if not set.
   */
  getCurrentTranslationLanguage(): string | null {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const match = document.cookie.match(/\bgoogtrans=([^;]+)/);
    if (!match) return null;
    const value = match[1].trim();
    if (!value || value === '/') return null;
    // Cookie format: /en/es or /en/zh-CN
    const parts = value.split('/').filter(Boolean);
    return parts.length >= 2 ? parts[1] : null;
  }

  /**
   * Re-triggers Google Translate so that newly added DOM content (e.g. from scroll/API) gets translated.
   * Call this after new content is rendered (e.g. after pushing to tempFlows and change detection).
   */
  retranslateForNewContent(): void {
    const langCode = this.getCurrentTranslationLanguage();
    if (!langCode) return;

    try {
      const hashValue = `googtrans(en|${langCode})`;
      const currentHash = window.location.hash;

      // Force Google to re-process the page by clearing hash then setting it again
      if (currentHash === `#${hashValue}`) {
        window.location.hash = '';
        setTimeout(() => {
          window.location.hash = hashValue;
          this.triggerGoogTeCombo(langCode);
          this.notifyAfterRetranslate();
        }, 50);
      } else {
        window.location.hash = hashValue;
        this.triggerGoogTeCombo(langCode);
        this.notifyAfterRetranslate();
      }
    } catch (e) {
      // no-op
    }
  }

  private triggerGoogTeCombo(langCode: string): void {
    setTimeout(() => {
      const combos = document.querySelectorAll('.goog-te-combo') as NodeListOf<HTMLSelectElement>;
      combos.forEach((el) => {
        if (el) {
          el.value = langCode;
          el.dispatchEvent(new Event('change', { bubbles: true }));
          el.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    }, 100);
  }

  private notifyAfterRetranslate(): void {
    setTimeout(() => this.afterRetranslate$.next(), 600);
  }
}
